import { ApiError } from "@/types/api";
import type { MyProfileResult, ChangePasswordRequest } from "./types";
import type { AxiosRequestConfig } from "axios";

import { apiClient } from "@/services/http/api-client";

/** Maintained wire bindings for current-school people management; forms and query state stay in the feature. */
export const peopleApi = {
  async staff(config?: AxiosRequestConfig): Promise<PeopleDirectoryResult> {
    return (
      await apiClient.get<PeopleDirectoryResult>(
        "/api/admin/people/staff",
        config,
      )
    ).data;
  },
  async students(
    classroomId: string,
    config?: AxiosRequestConfig,
  ): Promise<PeopleDirectoryResult> {
    return (
      await apiClient.get<PeopleDirectoryResult>("/api/admin/people/students", {
        ...config,
        params: { classroomId },
      })
    ).data;
  },
  async staffDetails(
    personId: string,
    config?: AxiosRequestConfig,
  ): Promise<StaffDetails> {
    return (
      await apiClient.get<StaffDetails>(
        `/api/admin/people/staff/${encodeURIComponent(personId)}`,
        config,
      )
    ).data;
  },
  async studentDetails(
    studentId: string,
    config?: AxiosRequestConfig,
  ): Promise<StudentDetails> {
    return (
      await apiClient.get<StudentDetails>(
        `/api/admin/people/students/${encodeURIComponent(studentId)}`,
        config,
      )
    ).data;
  },
  async updateStaff(
    personId: string,
    request: UpdateStaffRequest,
    config?: AxiosRequestConfig,
  ): Promise<StaffDetails> {
    return (
      await apiClient.put<StaffDetails>(
        `/api/admin/people/staff/${encodeURIComponent(personId)}`,
        request,
        config,
      )
    ).data;
  },
  async updateStudent(
    studentId: string,
    request: UpdateStudentRequest,
    config?: AxiosRequestConfig,
  ): Promise<StudentDetails> {
    return (
      await apiClient.put<StudentDetails>(
        `/api/admin/people/students/${encodeURIComponent(studentId)}`,
        request,
        config,
      )
    ).data;
  },
};
import type {
  PeopleDirectoryResult,
  StaffDetails,
  StudentDetails,
  UpdateStaffRequest,
  UpdateStudentRequest,
  ClassroomDirectoryResult,
  SmsRecipientRoster,
  SmsPreviewRequest,
  SmsPreviewPage,
  SmsCreditBalance,
  SmsSubmitRequest,
  SmsSubmissionResult,
  SmsJobDetails,
  SmsJobHistoryPage,
  SmsRosterParameters,
  ActiveClassroomsResult,
  AdminClassroomsResult,
  AnnotationPresentationResult,
  AnnotationWriterRequest,
  BindPresentationRequest,
  ClassroomStudentItem,
  ClassroomsDailyAbsenceReportResult,
  InsertLibraryRequest,
  InsertPresentationCommand,
  InsertPresentationResult,
  JoinSessionAsStudentCommand,
  JoinSessionAsAdminCommand,
  JoinSessionAsTeacherCommand,
  JoinSessionResult,
  LoginCommand,
  LoginResult,
  OpenAnnotationPageRequest,
  OpenAnnotationPageResult,
  RecordingsResult,
  SaveAnnotationPageRequest,
  SaveAnnotationPageResult,
  StudentClassroomResult,
  StudentTodayAttendanceResult,
  TeacherClassroomsResult,
} from "./types";

/**
 * Maintained HTTP bindings; only generated/schema.ts is emitted by api:generate.
 *
 * Direct consumers: Feature API modules (e.g. `src/features/<feature>/api/*.api.ts`).
 * Restricted: Do not import or call this client directly from UI components or pages.
 */

/** Authentication endpoints. */
export const authApi = {
  /** Returns only the authenticated person's safe account details. */
  async myProfile(config?: AxiosRequestConfig): Promise<MyProfileResult> {
    return (await apiClient.get<MyProfileResult>("/api/auth/me", config)).data;
  },
  /** Current-password verification errors are 400 responses, preserving the bearer session. */
  async changePassword(
    request: ChangePasswordRequest,
    config?: AxiosRequestConfig,
  ): Promise<void> {
    const response = await apiClient.post(
      "/api/auth/change-password",
      request,
      config,
    );
    if (response.status !== 204) throw new ApiError("invalid-response");
  },
  /** Refreshes the selected school's display metadata without issuing a new token. */
  async adminTitle(
    config?: AxiosRequestConfig,
  ): Promise<{ tenantId: string; adminTitle: string | null }> {
    return (
      await apiClient.get<{ tenantId: string; adminTitle: string | null }>(
        "/api/auth/admin-title",
        config,
      )
    ).data;
  },
  /**
   * Authenticates a user via username and password, returning an access token and tenant memberships.
   */
  async login(
    data: LoginCommand,
    config?: AxiosRequestConfig,
  ): Promise<LoginResult> {
    const response = await apiClient.post<LoginResult>(
      "/api/auth/login",
      data,
      config,
    );
    return response.data;
  },
};

/** Recording access is resolved by the backend from the active tenant and current memberships. */
export const recordingsApi = {
  /** Lists accessible videos with freshly signed URLs; no classroom/prefix input is accepted. */
  async getRecordings(config?: AxiosRequestConfig): Promise<RecordingsResult> {
    const response = await apiClient.get<RecordingsResult>(
      "/api/recordings",
      config,
    );
    return response.data;
  },
};

/** Live classroom session operations (BigBlueButton integration). */
export const sessionsApi = {
  /** Lists every classroom in the current admin school, with an optional ongoing meeting. */
  async getAdminClassrooms(
    config?: AxiosRequestConfig,
  ): Promise<AdminClassroomsResult> {
    const response = await apiClient.get<AdminClassroomsResult>(
      "/api/sessions/admin/classrooms",
      config,
    );
    return response.data;
  },

  /** Lists occupied meetings in the admin's active school; classroom access is resolved by the backend. */
  async getActiveClassrooms(
    config?: AxiosRequestConfig,
  ): Promise<ActiveClassroomsResult> {
    const response = await apiClient.get<ActiveClassroomsResult>(
      "/api/sessions/active",
      config,
    );
    return response.data;
  },

  /** Requests moderator access to an existing classroom meeting, preserving its opaque instance ID. */
  async joinAsAdmin(
    data: JoinSessionAsAdminCommand,
    config?: AxiosRequestConfig,
  ): Promise<JoinSessionResult> {
    const response = await apiClient.post<JoinSessionResult>(
      "/api/sessions/admin/join",
      data,
      config,
    );
    return response.data;
  },
  /**
   * Generates an attendee join URL for today's classroom session.
   */
  async joinAsStudent(
    data: JoinSessionAsStudentCommand,
    config?: AxiosRequestConfig,
  ): Promise<JoinSessionResult> {
    const response = await apiClient.post<JoinSessionResult>(
      "/api/sessions/student/join",
      data,
      config,
    );
    return response.data;
  },

  /**
   * Generates a moderator join URL for the verified teacher.
   */
  async joinAsTeacher(
    data: JoinSessionAsTeacherCommand,
    config?: AxiosRequestConfig,
  ): Promise<JoinSessionResult> {
    const response = await apiClient.post<JoinSessionResult>(
      "/api/sessions/teacher/join",
      data,
      config,
    );
    return response.data;
  },

  /**
   * Injects a remote presentation document into an active BigBlueButton session.
   */
  async insertPresentation(
    data: InsertPresentationCommand,
    config?: AxiosRequestConfig,
  ): Promise<InsertPresentationResult> {
    const response = await apiClient.post<InsertPresentationResult>(
      "/api/sessions/presentation/insert",
      data,
      config,
    );
    return response.data;
  },
};

/** Classroom roster and metadata operations. */
export const classroomsApi = {
  /** Reads the authorized school directory without a meeting-provider dependency. */
  async getDirectory(
    config?: AxiosRequestConfig,
  ): Promise<ClassroomDirectoryResult> {
    const response = await apiClient.get<ClassroomDirectoryResult>(
      "/api/classrooms",
      config,
    );
    return response.data;
  },
  /**
   * Retrieves the list of students enrolled in the specified classroom.
   */
  async getStudents(
    classroomId: string,
    config?: AxiosRequestConfig,
  ): Promise<ClassroomStudentItem[]> {
    const response = await apiClient.get<ClassroomStudentItem[]>(
      `/api/classrooms/${encodeURIComponent(classroomId)}/students`,
      config,
    );
    return response.data;
  },
};

/** Teacher profile and assignment operations. */
export const teachersApi = {
  /**
   * Retrieves active classrooms assigned to the authenticated teacher.
   */
  async getMyClassrooms(
    config?: AxiosRequestConfig,
  ): Promise<TeacherClassroomsResult> {
    const response = await apiClient.get<TeacherClassroomsResult>(
      "/api/teachers/me/classrooms",
      config,
    );
    return response.data;
  },
};

/** Student profile and assigned classroom operations. */
export const studentsApi = {
  /**
   * Retrieves the active classroom assigned to the authenticated student.
   */
  async getMyClassroom(
    config?: AxiosRequestConfig,
  ): Promise<StudentClassroomResult> {
    const response = await apiClient.get<StudentClassroomResult>(
      "/api/students/me/classroom",
      config,
    );
    return response.data;
  },

  /**
   * Retrieves today's presence intervals and evaluated attendance status for a student.
   *
   * @param studentId Unique identifier (UUID/GUID) of the student.
   * @param config Optional Axios request configuration including request scope headers and abort signal.
   */
  async getTodayAttendance(
    studentId: string,
    config?: AxiosRequestConfig,
  ): Promise<StudentTodayAttendanceResult> {
    const response = await apiClient.get<StudentTodayAttendanceResult>(
      `/api/students/${encodeURIComponent(studentId)}/attendance/today`,
      config,
    );
    return response.data;
  },
};

/**
 * Attendance report operations for school staff.
 *
 * Scoped to the tenant-wide absence report, which the backend reserves for the school Admin
 * role. A caller without that role receives 403; an unknown explicit `classroomId` receives 404.
 */
export const attendanceApi = {
  /**
   * Retrieves the daily absence report for every classroom in the active tenant.
   *
   * @param options Optional `date` (tenant-local, YYYY-MM-DD; omit for the school's today) and
   *   `classroomId` (restrict the report to a single classroom).
   * @param config Optional Axios request configuration including request scope headers and abort signal.
   */
  async getClassroomsDailyAbsence(
    options: {
      date?: string;
      classroomId?: string;
    } = {},
    config?: AxiosRequestConfig,
  ): Promise<ClassroomsDailyAbsenceReportResult> {
    const response = await apiClient.get<ClassroomsDailyAbsenceReportResult>(
      "/api/attendance/classrooms/daily-absence",
      {
        ...config,
        params: {
          ...config?.params,
          ...(options.date ? { date: options.date } : {}),
          ...(options.classroomId ? { classroomId: options.classroomId } : {}),
        },
      },
    );
    return response.data;
  },
};

/** Classroom annotation plugin synchronization operations. */
export const annotationsApi = {
  /**
   * Retrieves existing presentation bindings for this meeting.
   */
  async getPresentations(
    config?: AxiosRequestConfig,
  ): Promise<AnnotationPresentationResult[]> {
    const response = await apiClient.get<AnnotationPresentationResult[]>(
      "/api/annotations/presentations",
      config,
    );
    return response.data;
  },

  /**
   * Initiates catalog book insertion for annotations.
   */
  async insertLibrary(
    data: InsertLibraryRequest,
    config?: AxiosRequestConfig,
  ): Promise<AnnotationPresentationResult> {
    const response = await apiClient.post<AnnotationPresentationResult>(
      "/api/annotations/library/insert",
      data,
      config,
    );
    return response.data;
  },

  /**
   * Binds an observed BigBlueButton presentation ID to a durable library insertion intent.
   */
  async bindPresentation(
    id: string,
    data: BindPresentationRequest,
    config?: AxiosRequestConfig,
  ): Promise<void> {
    await apiClient.post<void>(
      `/api/annotations/presentations/${encodeURIComponent(id)}/bind`,
      data,
      config,
    );
  },

  /**
   * Opens a slide and acquires an initial writer lease for annotation.
   */
  async openPage(
    data: OpenAnnotationPageRequest,
    config?: AxiosRequestConfig,
  ): Promise<OpenAnnotationPageResult> {
    const response = await apiClient.post<OpenAnnotationPageResult>(
      "/api/annotations/pages/open",
      data,
      config,
    );
    return response.data;
  },

  /**
   * Renews an active writer lease to prevent expired browsers from mutating state.
   */
  async renewLease(
    id: string,
    data: AnnotationWriterRequest,
    config?: AxiosRequestConfig,
  ): Promise<void> {
    await apiClient.post<void>(
      `/api/annotations/documents/${encodeURIComponent(id)}/lease`,
      data,
      config,
    );
  },

  /**
   * Confirms client-side slide shape restoration before enabling saves.
   */
  async confirmPage(
    id: string,
    data: AnnotationWriterRequest,
    config?: AxiosRequestConfig,
  ): Promise<void> {
    await apiClient.post<void>(
      `/api/annotations/pages/${encodeURIComponent(id)}/confirm`,
      data,
      config,
    );
  },

  /**
   * Persists a slide annotation revision under optimistic concurrency control.
   */
  async savePage(
    id: string,
    data: SaveAnnotationPageRequest,
    config?: AxiosRequestConfig,
  ): Promise<SaveAnnotationPageResult> {
    const response = await apiClient.post<SaveAnnotationPageResult>(
      `/api/annotations/pages/${encodeURIComponent(id)}/save`,
      data,
      config,
    );
    return response.data;
  },
};

/** Administrator recipient discovery, previews, durable submission and progress reads. */
export const smsApi = {
  /** Reads available and held credits only for the authenticated active school. */
  async getCredits(config?: AxiosRequestConfig): Promise<SmsCreditBalance> {
    return (await apiClient.get<SmsCreditBalance>("/api/sms/credits", config))
      .data;
  },
  /** Reads a bounded page of tenant-owned job summaries, newest first. */
  async getJobs(
    page: number,
    pageSize: number,
    config?: AxiosRequestConfig,
    pollId?: string,
  ): Promise<SmsJobHistoryPage> {
    return (
      await apiClient.get<SmsJobHistoryPage>("/api/sms/jobs", {
        ...config,
        params: { page, pageSize, ...(pollId ? { pollId } : {}) },
      })
    ).data;
  },
  /** Saves a send intent once; callers retain requestId for explicit retries. */
  async submitJob(
    data: SmsSubmitRequest,
    config?: AxiosRequestConfig,
  ): Promise<SmsSubmissionResult> {
    return (
      await apiClient.post<SmsSubmissionResult>("/api/sms/jobs", data, config)
    ).data;
  },
  /** Reads progress through a fixed backend route, never by following an untrusted status URL. */
  async getJob(
    jobId: string,
    page: number,
    pageSize: number,
    config?: AxiosRequestConfig,
  ): Promise<SmsJobDetails> {
    return (
      await apiClient.get<SmsJobDetails>(
        `/api/sms/jobs/${encodeURIComponent(jobId)}`,
        {
          ...config,
          params: { page, pageSize },
        },
      )
    ).data;
  },
  /** Resolves personalized messages on the server without creating or dispatching an SMS job. */
  async preview(
    data: SmsPreviewRequest,
    page: number,
    pageSize: number,
    config?: AxiosRequestConfig,
  ): Promise<SmsPreviewPage> {
    const response = await apiClient.post<SmsPreviewPage>(
      "/api/sms/preview",
      data,
      {
        ...config,
        params: { page, pageSize },
      },
    );
    return response.data;
  },
  /** Reads every person in one selected group; no pagination or client-side page loop is used. */
  async getRecipientRoster(
    params: SmsRosterParameters,
    config?: AxiosRequestConfig,
  ): Promise<SmsRecipientRoster> {
    const response = await apiClient.get<SmsRecipientRoster>(
      "/api/sms/recipients/roster",
      { ...config, params },
    );
    return response.data;
  },
};
